import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

export default function RefundPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="refund-page">
      <header className="border-b border-slate-100 sticky top-0 z-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/pricing" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
            <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center"><Shield className="w-5 h-5 text-white" strokeWidth={2} /></div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>AgentCheck</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="refund-policy">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Refund & Cancellation Policy</h1>
        <p className="text-sm text-slate-500 mb-10">Effective date: February 19, 2026</p>

        <div className="space-y-8 text-[15px] text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>1. Cancellation</h2>
            <p>You may cancel your subscription at any time through the <strong>Manage Billing</strong> link on the <Link to="/pricing" className="text-[#007AFF] underline">Pricing page</Link>, or by contacting <a href="mailto:support@agentcheck.com" className="text-[#007AFF] underline">support@agentcheck.com</a>.</p>
            <p className="mt-2">When you cancel:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Your subscription will remain active until the end of your current billing period.</li>
              <li>You will not be charged again after cancellation.</li>
              <li>All paid features (Pro, Teams, or Verify) will remain accessible until the period ends.</li>
              <li>After the period ends, your account will revert to the Free plan.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>2. 7-Day Refund Window</h2>
            <p>We offer a <strong>full refund within 7 days</strong> of your initial purchase if you are not satisfied with the service.</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Eligible:</strong> First-time purchases within 7 calendar days of payment.</li>
              <li><strong>How to request:</strong> Email <a href="mailto:support@agentcheck.com?subject=Refund%20Request" className="text-[#007AFF] underline">support@agentcheck.com</a> with the subject &quot;Refund Request&quot; and include your payment email or device ID.</li>
              <li><strong>Processing time:</strong> Refunds are processed within 5&ndash;10 business days via Stripe to your original payment method.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>3. After the 7-Day Window</h2>
            <p>After the initial 7-day period:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Monthly plans:</strong> No prorated refunds. Cancel anytime and access continues until the end of the billing cycle.</li>
              <li><strong>Annual plans:</strong> We may issue a prorated refund at our discretion for unused months. Contact support to discuss.</li>
              <li><strong>Exceptional circumstances:</strong> We handle billing errors, duplicate charges, and service outages on a case-by-case basis. Contact us and we&apos;ll make it right.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>4. Payment Failures & Grace Period</h2>
            <p>If a payment fails (e.g., expired card), we provide a <strong>7-day grace period</strong> before downgrading your plan:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>During the grace period, all paid features remain active.</li>
              <li>Stripe will automatically retry the charge.</li>
              <li>If payment is not resolved within 7 days, your account reverts to the Free plan.</li>
              <li>No data is deleted during the grace period. You can upgrade again at any time.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>5. Verify (Server Logs) Plans</h2>
            <p>For Verify Merchant, Verify Plus, and Commerce Ops plans:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>The same 7-day refund window applies to your first purchase.</li>
              <li>Uploaded log files and generated reports remain accessible until retention expires (30/90/180 days depending on plan).</li>
              <li>You may delete your uploaded data at any time, regardless of plan status.</li>
              <li>Overage charges (if applicable) are non-refundable once processing has occurred.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>6. How to Get Help</h2>
            <p>For any billing questions, disputes, or refund requests:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Email: <a href="mailto:support@agentcheck.com" className="text-[#007AFF] underline">support@agentcheck.com</a></li>
              <li>Subject line: &quot;Billing&quot; or &quot;Refund Request&quot;</li>
              <li>Include your payment email or device ID for faster resolution.</li>
            </ul>
            <p className="mt-3">We aim to respond to all billing inquiries within 24 hours on business days.</p>
          </section>

          <section className="bg-slate-50 rounded-xl p-5">
            <p className="text-sm text-slate-600"><strong>Our commitment:</strong> We want you to pay only for value you receive. If something isn&apos;t working, tell us before canceling &mdash; we&apos;ll try to fix it. If we can&apos;t, we&apos;ll refund you.</p>
          </section>
        </div>
      </main>
    </div>
  );
}
